/**
 * The route table. Every page is lazy-loaded.
 * Scope-level gating ensures users can only access pages they're allowed to see.
 */
import React, { Suspense }                       from "react";
import { Navigate, Route, Routes }               from "react-router-dom";
import { useAuth }                               from "@features/auth/AuthContext";
import { AppShell, PlatformShell }               from "@app/layouts";
import { ErrorBoundary }                         from "@app/ErrorBoundary";
import {
  RequireAuth,
  RequirePerm,
  RequireScope,
  RequireExactScope,
  RequireModule,
  landingPathFor,
} from "./guards";

import { AppShellSkeleton, PageContentSkeleton } from "@app/AppShellSkeleton";
import { IS_ONPREM } from "@shared/config/edition";

/** Stand-in for pages that do not exist in this edition (never routed to). */
const NotInEdition: React.FC = () => <Navigate to="/" replace />;

const LoginPage = React.lazy(() => import("@features/auth/LoginPage"));
const GroupsPage = IS_ONPREM
  ? NotInEdition
  : React.lazy(() => import("@features/groups/GroupsPage"));
const OrganizationsPage = IS_ONPREM
  ? NotInEdition
  : React.lazy(() => import("@features/organizations/OrganizationsPage"));
const DashboardPage = IS_ONPREM
  ? NotInEdition
  : React.lazy(() => import("@features/dashboard/DashboardPage"));
const OrgDashboardPage = IS_ONPREM
  ? NotInEdition
  : React.lazy(() => import("@features/dashboard/OrgDashboardPage"));
const AgentEnrollmentPage = React.lazy(
  () => import("@features/agents/AgentEnrollmentPage"),
);
const FimManagementPage = React.lazy(
  () => import("@features/fim/FimManagementPage"),
);
const FimMonitoringPage = React.lazy(
  () => import("@features/fim/FimMonitoringPage"),
);
const FimAgentPickerPage = React.lazy(
  () => import("@features/fim/FimAgentPickerPage"),
);
const UserManagementPage = React.lazy(
  () => import("@features/users/UserManagementPage"),
);

/* ---- placeholder pages (wired flow; content ships per module) ---- */
const GroupDashboardPage = IS_ONPREM
  ? NotInEdition
  : React.lazy(() => import("@features/group-dashboard/GroupDashboardPage"));
const EndpointInventoryPage = React.lazy(
  () => import("@/features/endpoint-inventory/EndpointInventoryPage"),
);
const AgentDashboardPage = React.lazy(
  () => import("@features/agent-dashboard/AgentDashboardPage"),
);
const ResourceMonitoringPage = IS_ONPREM
  ? NotInEdition
  : React.lazy(() => import("@features/resource-monitoring/ResourceMonitoringPage"));
const SystemMonitoringPage = React.lazy(
  () => import("@features/system-monitoring/SystemMonitoringPage"),
);
const CisBenchmarkPage = React.lazy(
  () => import("@features/cis-benchmark/CisBenchmarkPage"),
);
const CompliancePage = React.lazy(
  () => import("@features/compliance/CompliancePage"),
);
const VulnerabilityDetectionPage = React.lazy(
  () => import("@/features/vulnerability-detection/VulnerabilityDetectionPage"),
);
const MitreAttackPage = IS_ONPREM
  ? NotInEdition
  : React.lazy(() => import("@features/mitre-attack/MitreAttackPage"));
const AuthenticationAccessPage = React.lazy(
  () => import("@features/auth-access/AuthAccessPage"),
);
const ThreatHuntingPage = React.lazy(
  () => import("@features/threat-hunting/ThreatHuntingPage"),
);
const AlertThresholdsPage = React.lazy(
  () => import("@features/resource-alerts/AlertThresholdsPage"),
);
const EventVolumePage = React.lazy(
  () => import("@features/event-volume/EventVolumePage"),
);
// On-prem only: licence details page.
const LicencePage = IS_ONPREM
  ? React.lazy(() => import("@features/licence/LicencePage"))
  : NotInEdition;
const SettingsPage = IS_ONPREM
  ? NotInEdition
  : React.lazy(() => import("@features/settings/SettingsPage"));

const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireAuth>
    <AppShell>
      <ErrorBoundary>{children}</ErrorBoundary>
    </AppShell>
  </RequireAuth>
);

/**
 * The platform-admin frame: header only, no sidebar/topnav. `title` feeds the
 * Header since there's no nav model to derive it from here.
 */
const PlatformPage: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <RequireAuth>
    <RequireScope scope="platform">
      <PlatformShell title={title}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </PlatformShell>
    </RequireScope>
  </RequireAuth>
);

/**
 * A route both a platform admin and a group admin can reach (e.g.
 * /organizations), but which should wear different chrome: the platform admin
 * gets the bare header-only frame, everyone else the full AppShell. The page
 * itself already renders read-only vs editable by scope, so only the frame
 * differs here.
 */
const ScopedPage: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const { user } = useAuth();
  if (user?.scope_level === "platform") {
    return (
      <RequireAuth>
        <PlatformShell title={title}>
          <ErrorBoundary>{children}</ErrorBoundary>
        </PlatformShell>
      </RequireAuth>
    );
  }
  return (
    <RequireAuth>
      <AppShell>
        <ErrorBoundary>{children}</ErrorBoundary>
      </AppShell>
    </RequireAuth>
  );
};

export const AppRoutes: React.FC = () => {
  const { user, initialising } = useAuth();
  // if (initialising) return <FullPageSpinner />;

  if (initialising) return <AppShellSkeleton />;


  return (
    // <Suspense fallback={<FullPageSpinner />}>
    <Suspense fallback={<PageContentSkeleton />}>
      <Routes>
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to={landingPathFor(user)} replace />
            ) : (
              <LoginPage />
            )
          }
        />

        {/* Platform scope — super_admin only. Header-only frame. */}
        {!IS_ONPREM && (
        <Route
          path="/groups"
          element={
            <PlatformPage title="Group Management">
              <RequirePerm perm="group:read">
                <GroupsPage />
              </RequirePerm>
            </PlatformPage>
          }
        />
        )}

        {/* Group scope — tenant_admin + super_admin. Platform admin gets the
            bare frame and a read-only page; group admin gets the full shell. */}
        {!IS_ONPREM && (
        <Route
          path="/organizations"
          element={
            <ScopedPage title="Organizations">
              <RequireScope scope="group">
                <RequirePerm perm="org:read">
                  <OrganizationsPage />
                </RequirePerm>
              </RequireScope>
            </ScopedPage>
          }
        />
        )}

        {/* ---- org scope: only org_admin / viewer ---- */}
        {!IS_ONPREM && (
        <Route
          path="/org-dashboard"
          element={
            <Page>
              <RequireExactScope scope="org">
                <OrgDashboardPage />
              </RequireExactScope>
            </Page>
          }
        />
        )}
        <Route
          path="/agent-enrollment"
          element={
            <Page>
              <RequireExactScope scope="org">
                <RequirePerm perm="agent:enroll">
                  <AgentEnrollmentPage />
                </RequirePerm>
              </RequireExactScope>
            </Page>
          }
        />
        <Route
          path="/fim-monitoring"
          element={
            <Page>
              <RequireModule module="fim">
                <FimAgentPickerPage />
              </RequireModule>
            </Page>
          }
        />
        {/* FIM monitoring page. Lives at /fim (not /agents/:agentId/fim) to
            match the pattern of every other analysis page — the selected
            agent comes from useSelectedClient() inside the page, not the URL.
            Open to any signed-in user: the server verifies the agent belongs
            to an org they can access and returns 403 otherwise, so there is
            no client-side scope guard to duplicate. The module guard hides
            it when the org's FIM module is off. */}
        <Route
          path="/file-integrity-monitoring"
          element={
            <Page>
              <RequireModule module="fim">
                <FimMonitoringPage />
              </RequireModule>
            </Page>
          }
        />
        <Route
          path="/fim-management"
          element={
            <Page>
              <RequireExactScope scope="org">
                <RequirePerm perm="agent:read">
                  <RequireModule module="fim">
                    <FimManagementPage />
                  </RequireModule>
                </RequirePerm>
              </RequireExactScope>
            </Page>
          }
        />
        <Route
          path="/user-management"
          element={
            <Page>
              <RequireExactScope scope="org">
                <RequirePerm perm="user:read">
                  <UserManagementPage />
                </RequirePerm>
              </RequireExactScope>
            </Page>
          }
        />
        <Route
          path="/alert-thresholds"
          element={
            <Page>
              <RequireExactScope scope="org">
                <RequireModule module="sysmon">
                  <RequirePerm perm="alerts:read">
                    <AlertThresholdsPage />
                  </RequirePerm>
                </RequireModule>
              </RequireExactScope>
            </Page>
          }
        />
        <Route
          path="/event-volume"
          element={
            <Page>
              <RequireExactScope scope="org">
                <RequirePerm perm="events:read">
                  <EventVolumePage />
                </RequirePerm>
              </RequireExactScope>
            </Page>
          }
        />
        {IS_ONPREM && (
          // US spelling forwards to the page
          <Route path="/license" element={<Navigate to="/licence" replace />} />
        )}
        {IS_ONPREM && (
          <Route
            path="/licence"
            element={
              <Page>
                <RequireExactScope scope="org">
                  <LicencePage />
                </RequireExactScope>
              </Page>
            }
          />
        )}

        {/* ---- group scope ---- */}
        {!IS_ONPREM && (
        <Route
          path="/group-dashboard"
          element={
            <Page>
              <RequireScope scope="group">
                <RequirePerm perm="org:read">
                  <GroupDashboardPage />
                </RequirePerm>
              </RequireScope>
            </Page>
          }
        />
        )}

        {/* ---- org: posture / monitoring / detection (org admin + viewer) ----
            Gate on the same read permission a viewer holds; module-scoped
            pages additionally require the org to have that module enabled. */}
        <Route
          path="/endpoint-inventory"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <EndpointInventoryPage />
              </RequirePerm>
            </Page>
          }
        />
        <Route
          path="/agent-dashboard"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <AgentDashboardPage />
              </RequirePerm>
            </Page>
          }
        />
        {!IS_ONPREM && (
        <Route
          path="/resource-monitoring"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <RequireModule module="sysmon">
                  <ResourceMonitoringPage />
                </RequireModule>
              </RequirePerm>
            </Page>
          }
        />
        )}
        <Route
          path="/system-monitoring"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <RequireModule module="sysmon">
                  <SystemMonitoringPage />
                </RequireModule>
              </RequirePerm>
            </Page>
          }
        />
        <Route
          path="/cis-benchmark"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <RequireModule module="cis">
                  <CisBenchmarkPage />
                </RequireModule>
              </RequirePerm>
            </Page>
          }
        />
        <Route
          path="/compliance"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <RequireModule module="compliance">
                  <CompliancePage />
                </RequireModule>
              </RequirePerm>
            </Page>
          }
        /> 
        
        <Route
          path="/vulnerability-detection"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <RequireModule module="vuln">
                  <VulnerabilityDetectionPage />
                </RequireModule>
              </RequirePerm>
            </Page>

          }
        />
        {!IS_ONPREM && (
        <Route
          path="/mitre-attack"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <RequireModule module="mitre">
                  <MitreAttackPage />
                </RequireModule>
              </RequirePerm>
            </Page>
          }
        />
        )}
        <Route
          path="/authentication-access"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <AuthenticationAccessPage />
              </RequirePerm>
            </Page>
          }
        />
        <Route
          path="/threat-hunting"
          element={
            <Page>
              <RequirePerm perm="agent:read">
                <RequireModule module="threat">
                  <ThreatHuntingPage />
                </RequireModule>
              </RequirePerm>
            </Page>
          }
        />

        {/* ---- platform settings ---- */}
        {!IS_ONPREM && (
        <Route
          path="/settings"
          element={
            <Page>
              <RequirePerm perm="config:manage">
                <SettingsPage />
              </RequirePerm>
            </Page>
          }
        />
        )}

        {/* Dashboard placeholder — any authenticated user */}
        {!IS_ONPREM && (
        <Route
          path="/dashboard"
          element={
            <Page>
              <DashboardPage />
            </Page>
          }
        />
        )}
        {!IS_ONPREM && (
        <Route
          path="/dashboard/:groupId"
          element={
            <Page>
              <DashboardPage />
            </Page>
          }
        />
        )}

        {/* Role-aware home */}
        <Route
          path="/"
          element={<Navigate to={landingPathFor(user)} replace />}
        />

        {/* Unknown route -> /login. If the visitor is signed in, the /login
            route above immediately bounces them on to their landing page, so
            an authenticated typo never strands anyone on a blank screen. */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
};


// /**
//  * The route table. Every page is lazy-loaded.
//  * Scope-level gating ensures users can only access pages they're allowed to see.
//  */
// import React, { Suspense }                       from "react";
// import { Navigate, Route, Routes }               from "react-router-dom";
// import { useAuth }                               from "@features/auth/AuthContext";
// import { AppShell, PlatformShell }               from "@app/layouts";
// import { ErrorBoundary }                         from "@app/ErrorBoundary";
// import {
//   RequireAuth,
//   RequirePerm,
//   RequireScope,
//   RequireExactScope,
//   RequireModule,
//   landingPathFor,
// } from "./guards";

// import { AppShellSkeleton, PageContentSkeleton } from "@app/AppShellSkeleton";

// const LoginPage = React.lazy(() => import("@features/auth/LoginPage"));
// const GroupsPage = React.lazy(() => import("@features/groups/GroupsPage"));
// const OrganizationsPage = React.lazy(
//   () => import("@features/organizations/OrganizationsPage"),
// );
// const DashboardPage = React.lazy(
//   () => import("@features/dashboard/DashboardPage"),
// );
// const OrgDashboardPage = React.lazy(
//   () => import("@features/dashboard/OrgDashboardPage"),
// );
// const AgentEnrollmentPage = React.lazy(
//   () => import("@features/agents/AgentEnrollmentPage"),
// );
// const FimManagementPage = React.lazy(
//   () => import("@features/fim/FimManagementPage"),
// );
// const FimMonitoringPage = React.lazy(
//   () => import("@features/fim/FimMonitoringPage"),
// );
// const FimAgentPickerPage = React.lazy(
//   () => import("@features/fim/FimAgentPickerPage"),
// );
// const UserManagementPage = React.lazy(
//   () => import("@features/users/UserManagementPage"),
// );

// /* ---- placeholder pages (wired flow; content ships per module) ---- */
// const GroupDashboardPage = React.lazy(
//   () => import("@features/group-dashboard/GroupDashboardPage"),
// );
// const EndpointInventoryPage = React.lazy(
//   () => import("@features/endpoint-inventory/EndpointInventoryPage"),
// );
// const AgentDashboardPage = React.lazy(
//   () => import("@features/agent-dashboard/AgentDashboardPage"),
// );
// const ResourceMonitoringPage = React.lazy(
//   () => import("@features/resource-monitoring/ResourceMonitoringPage"),
// );
// const SystemMonitoringPage = React.lazy(
//   () => import("@features/system-monitoring/SystemMonitoringPage"),
// );
// const CisBenchmarkPage = React.lazy(
//   () => import("@features/cis-benchmark/CisBenchmarkPage"),
// );
// const CompliancePage = React.lazy(
//   () => import("@features/compliance/CompliancePage"),
// );
// const VulnerabilitiesPage = React.lazy(
//   () => import("@features/vulnerabilities/VulnerabilitiesPage"),
// );
// const MitreAttackPage = React.lazy(
//   () => import("@features/mitre-attack/MitreAttackPage"),
// );
// const AuthenticationAccessPage = React.lazy(
//   () => import("@features/auth-access/AuthAccessPage"),
// );
// const ThreatHuntingPage = React.lazy(
//   () => import("@features/threat-hunting/ThreatHuntingPage"),
// );
// const AlertThresholdsPage = React.lazy(
//   () => import("@features/resource-alerts/AlertThresholdsPage"),
// );
// const EventVolumePage = React.lazy(
//   () => import("@features/event-volume/EventVolumePage"),
// );
// const SettingsPage = React.lazy(
//   () => import("@features/settings/SettingsPage"),
// );

// const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
//   <RequireAuth>
//     <AppShell>
//       <ErrorBoundary>{children}</ErrorBoundary>
//     </AppShell>
//   </RequireAuth>
// );

// /**
//  * The platform-admin frame: header only, no sidebar/topnav. `title` feeds the
//  * Header since there's no nav model to derive it from here.
//  */
// const PlatformPage: React.FC<{ title: string; children: React.ReactNode }> = ({
//   title,
//   children,
// }) => (
//   <RequireAuth>
//     <RequireScope scope="platform">
//       <PlatformShell title={title}>
//         <ErrorBoundary>{children}</ErrorBoundary>
//       </PlatformShell>
//     </RequireScope>
//   </RequireAuth>
// );

// /**
//  * A route both a platform admin and a group admin can reach (e.g.
//  * /organizations), but which should wear different chrome: the platform admin
//  * gets the bare header-only frame, everyone else the full AppShell. The page
//  * itself already renders read-only vs editable by scope, so only the frame
//  * differs here.
//  */
// const ScopedPage: React.FC<{ title: string; children: React.ReactNode }> = ({
//   title,
//   children,
// }) => {
//   const { user } = useAuth();
//   if (user?.scope_level === "platform") {
//     return (
//       <RequireAuth>
//         <PlatformShell title={title}>
//           <ErrorBoundary>{children}</ErrorBoundary>
//         </PlatformShell>
//       </RequireAuth>
//     );
//   }
//   return (
//     <RequireAuth>
//       <AppShell>
//         <ErrorBoundary>{children}</ErrorBoundary>
//       </AppShell>
//     </RequireAuth>
//   );
// };

// export const AppRoutes: React.FC = () => {
//   const { user, initialising } = useAuth();
//   // if (initialising) return <FullPageSpinner />;

//   if (initialising) return <AppShellSkeleton />;


//   return (
//     // <Suspense fallback={<FullPageSpinner />}>
//     <Suspense fallback={<PageContentSkeleton />}>
//       <Routes>
//         <Route
//           path="/login"
//           element={
//             user ? (
//               <Navigate to={landingPathFor(user)} replace />
//             ) : (
//               <LoginPage />
//             )
//           }
//         />

//         {/* Platform scope — super_admin only. Header-only frame. */}
//         <Route
//           path="/groups"
//           element={
//             <PlatformPage title="Group Management">
//               <RequirePerm perm="group:read">
//                 <GroupsPage />
//               </RequirePerm>
//             </PlatformPage>
//           }
//         />

//         {/* Group scope — tenant_admin + super_admin. Platform admin gets the
//             bare frame and a read-only page; group admin gets the full shell. */}
//         <Route
//           path="/organizations"
//           element={
//             <ScopedPage title="Organizations">
//               <RequireScope scope="group">
//                 <RequirePerm perm="org:read">
//                   <OrganizationsPage />
//                 </RequirePerm>
//               </RequireScope>
//             </ScopedPage>
//           }
//         />

//         {/* ---- org scope: only org_admin / viewer ---- */}
//         <Route
//           path="/org-dashboard"
//           element={
//             <Page>
//               <RequireExactScope scope="org">
//                 <OrgDashboardPage />
//               </RequireExactScope>
//             </Page>
//           }
//         /> 
//         <Route
//           path="/agent-enrollment"
//           element={
//             <Page>
//               <RequireExactScope scope="org">
//                 <RequirePerm perm="agent:enroll">
//                   <AgentEnrollmentPage />
//                 </RequirePerm>
//               </RequireExactScope>
//             </Page>
//           }
//         />
//         <Route
//           path="/fim-monitoring"
//           element={
//             <Page>
//               <RequireModule module="fim">
//                 <FimAgentPickerPage />
//               </RequireModule>
//             </Page>
//           } 
//         />
//         {/* Per-agent FIM dashboard. Open to any signed-in user: the server
//             verifies the agent belongs to an org they can access and returns
//             403 otherwise, so there is no client-side scope guard to duplicate.
//             The module guard hides it when the org's FIM module is off. */}
//         <Route
//           path="/agents/:agentId/fim"
//           element={
//             <Page>
//               <RequireModule module="fim">
//                 <FimMonitoringPage />
//               </RequireModule>
//             </Page>
//           }
//         />
//         <Route
//           path="/fim-management"
//           element={
//             <Page>
//               <RequireExactScope scope="org">
//                 <RequirePerm perm="agent:read">
//                   <RequireModule module="fim">
//                     <FimManagementPage />
//                   </RequireModule>
//                 </RequirePerm>
//               </RequireExactScope>
//             </Page>
//           }
//         />
//         <Route
//           path="/user-management"
//           element={
//             <Page>
//               <RequireExactScope scope="org">
//                 <RequirePerm perm="user:read">
//                   <UserManagementPage />
//                 </RequirePerm>
//               </RequireExactScope>
//             </Page>
//           }
//         />
//         <Route
//           path="/alert-thresholds"
//           element={
//             <Page>
//               <RequireExactScope scope="org">
//                 <RequireModule module="sysmon">
//                   <RequirePerm perm="alerts:read">
//                     <AlertThresholdsPage />
//                   </RequirePerm>
//                 </RequireModule>
//               </RequireExactScope>
//             </Page>
//           }
//         />
//         <Route
//           path="/event-volume"
//           element={
//             <Page>
//               <RequireExactScope scope="org">
//                 <RequirePerm perm="events:read">
//                   <EventVolumePage />
//                 </RequirePerm>
//               </RequireExactScope>
//             </Page>
//           }
//         />

//         {/* ---- group scope ---- */}
//         <Route
//           path="/group-dashboard"
//           element={
//             <Page>
//               <RequireScope scope="group">
//                 <RequirePerm perm="org:read">
//                   <GroupDashboardPage />
//                 </RequirePerm>
//               </RequireScope>
//             </Page>
//           }
//         />

//         {/* ---- org: posture / monitoring / detection (org admin + viewer) ----
//             Gate on the same read permission a viewer holds; module-scoped
//             pages additionally require the org to have that module enabled. */}
//         <Route
//           path="/endpoint-inventory"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <EndpointInventoryPage />
//               </RequirePerm>
//             </Page>
//           }
//         />
//         <Route
//           path="/agent-dashboard"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <AgentDashboardPage />
//               </RequirePerm>
//             </Page>
//           }
//         />
//         <Route
//           path="/resource-monitoring"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <RequireModule module="sysmon">
//                   <ResourceMonitoringPage />
//                 </RequireModule>
//               </RequirePerm>
//             </Page>
//           }
//         />
//         <Route
//           path="/system-monitoring"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <RequireModule module="sysmon">
//                   <SystemMonitoringPage />
//                 </RequireModule>
//               </RequirePerm>
//             </Page>
//           }
//         />
//         <Route
//           path="/cis-benchmark"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <RequireModule module="cis">
//                   <CisBenchmarkPage />
//                 </RequireModule>
//               </RequirePerm>
//             </Page>
//           }
//         />
//         <Route
//           path="/compliance"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <RequireModule module="compliance">
//                   <CompliancePage />
//                 </RequireModule>
//               </RequirePerm>
//             </Page>
//           }
//         /> 
        
//         <Route
//           path="/vulnerabilities"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <RequireModule module="vuln">
//                   <VulnerabilitiesPage />
//                 </RequireModule>
//               </RequirePerm>
//             </Page>

//           }
//         />
//         <Route
//           path="/mitre-attack"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <RequireModule module="mitre">
//                   <MitreAttackPage />
//                 </RequireModule>
//               </RequirePerm>
//             </Page>
//           }
//         />
//         <Route
//           path="/authentication-access"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <AuthenticationAccessPage />
//               </RequirePerm>
//             </Page>
//           }
//         />
//         <Route
//           path="/threat-hunting"
//           element={
//             <Page>
//               <RequirePerm perm="agent:read">
//                 <RequireModule module="threat">
//                   <ThreatHuntingPage />
//                 </RequireModule>
//               </RequirePerm>
//             </Page>
//           }
//         />

//         {/* ---- platform settings ---- */}
//         <Route
//           path="/settings"
//           element={
//             <Page>
//               <RequirePerm perm="config:manage">
//                 <SettingsPage />
//               </RequirePerm>
//             </Page>
//           }
//         />

//         {/* Dashboard placeholder — any authenticated user */}
//         <Route
//           path="/dashboard"
//           element={
//             <Page>
//               <DashboardPage />
//             </Page>
//           }
//         />
//         <Route
//           path="/dashboard/:groupId"
//           element={
//             <Page>
//               <DashboardPage />
//             </Page>
//           }
//         />

//         {/* Role-aware home */}
//         <Route
//           path="/"
//           element={<Navigate to={landingPathFor(user)} replace />}
//         />

//         {/* Unknown route -> /login. If the visitor is signed in, the /login
//             route above immediately bounces them on to their landing page, so
//             an authenticated typo never strands anyone on a blank screen. */}
//         <Route path="*" element={<Navigate to="/login" replace />} />
//       </Routes>
//     </Suspense>
//   );
// };
